import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Flame, ChevronLeft, Activity, CreditCard, Link2Off, Salad, Target } from "lucide-react";
import api, { formatError, track } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const ALLERGENS = ["gluten", "lactose", "fruits à coque", "œufs", "poisson", "crustacés", "soja", "arachide"];
const DIET_TYPES = ["omnivore", "végétarien", "végan", "sans porc", "pescétarien"];

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Chips({ options, value, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = (value || []).includes(o);
        return (
          <button key={o} type="button" onClick={() => onToggle(o)}
            className={`px-4 h-11 rounded-md border text-sm font-medium capitalize transition-colors active:scale-95 ${active ? "bg-primary border-primary text-white" : "bg-secondary border-border text-foreground hover:border-primary/50"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function Settings() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [strava, setStrava] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [prof, setProf] = useState(null);
  const [savingProf, setSavingProf] = useState(false);
  const [obj, setObj] = useState({ target_time: "", race_date: "", note: "" });
  const [objBusy, setObjBusy] = useState(false);
  const [objPending, setObjPending] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/integrations/strava/status");
      setStrava(data);
    } catch { setStrava({ connected: false }); }
    finally { setLoading(false); }
  };

  const loadProfile = async () => {
    try {
      const { data } = await api.get("/profile");
      setProf({
        weight_kg: data.weight_kg ?? "", height_cm: data.height_cm ?? "", age: data.age ?? "",
        gender: data.gender || "homme", weekly_hours: data.weekly_hours ?? "",
        diet_type: data.diet_type || "omnivore", allergies: data.allergies || [],
        allergies_other: data.allergies_other || "", liked_foods: data.liked_foods || "",
        disliked_foods: data.disliked_foods || "", about_me: data.about_me || "",
      });
      setObj({ target_time: data.target_time || "", race_date: data.race_date || "", note: "" });
      const { data: reqs } = await api.get("/objective-request");
      setObjPending(reqs?.request || null);
    } catch { setProf({ weight_kg: "", height_cm: "", age: "", gender: "homme", weekly_hours: "", diet_type: "omnivore", allergies: [], allergies_other: "", liked_foods: "", disliked_foods: "", about_me: "" }); }
  };

  const setP = (k) => (e) => setProf((p) => ({ ...p, [k]: e.target.value }));
  const toggleAllergy = (v) => setProf((p) => ({ ...p, allergies: p.allergies.includes(v) ? p.allergies.filter((x) => x !== v) : [...p.allergies, v] }));

  const saveProfile = async () => {
    setSavingProf(true);
    try {
      const payload = {
        ...prof,
        weight_kg: prof.weight_kg === "" ? null : Number(prof.weight_kg),
        height_cm: prof.height_cm === "" ? null : Number(prof.height_cm),
        age: prof.age === "" ? null : Number(prof.age),
        weekly_hours: prof.weekly_hours === "" ? null : Number(prof.weekly_hours),
      };
      const { data } = await api.post("/profile", payload);
      toast.success(data.nutrition_recalculated ? "Profil enregistré — cibles macro recalculées et recettes régénérées." : "Profil enregistré.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setSavingProf(false); }
  };

  const sendObjectiveChange = async () => {
    setObjBusy(true);
    try {
      const { data } = await api.post("/profile/objective-change", {
        target_time: obj.target_time || null, race_date: obj.race_date || null, note: obj.note || null,
      });
      setObjPending(data.request);
      setObj((o) => ({ ...o, note: "" }));
      toast.success("Demande envoyée à ton coach. Il la validera avant de relancer la génération.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setObjBusy(false); }
  };

  useEffect(() => {
    loadStatus();
    if (user?.role !== "coach") loadProfile();
    const s = params.get("strava");
    if (s === "connected") { toast.success("Strava connecté 🎉"); track("strava_connect", {}); params.delete("strava"); setParams(params, { replace: true }); }
    else if (s === "error") { toast.error("La connexion Strava a échoué. Réessaie."); params.delete("strava"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  const connectStrava = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/strava/connect");
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const disconnectStrava = async () => {
    setBusy(true);
    try {
      await api.post("/integrations/strava/disconnect", {});
      toast.success("Strava déconnecté.");
      await loadStatus();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const manageSubscription = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-border">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => nav("/app")}>
          <Flame className="text-primary" size={22} strokeWidth={2.5} />
          <span className="font-display text-xl">ROX<span className="text-primary">LAB</span></span>
        </div>
        <button data-testid="settings-back-btn" onClick={() => nav("/app")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft size={16} /> Retour
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8 space-y-5">
        <h1 className="font-display text-3xl">Paramètres</h1>

        {/* Strava */}
        <div className="bg-card border border-border rounded-xl p-5" data-testid="strava-card">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-10 rounded-lg bg-[#FC4C02]/15 flex items-center justify-center"><Activity className="text-[#FC4C02]" size={20} /></span>
            <div>
              <h2 className="font-display text-xl leading-none">Strava</h2>
              <p className="text-xs text-muted-foreground mt-1">Synchronise tes courses pour remplir automatiquement temps et allure.</p>
            </div>
          </div>
          {loading ? (
            <div className="h-10 bg-secondary/60 rounded-md animate-pulse mt-3" />
          ) : strava?.connected ? (
            <div className="mt-3">
              <div className="flex items-center gap-2 text-sm text-green-400 mb-3" data-testid="strava-connected-state">
                <span className="w-2 h-2 rounded-full bg-green-400" /> Connecté{strava.athlete_name ? ` — ${strava.athlete_name}` : ""}
              </div>
              <Button data-testid="strava-disconnect-btn" onClick={disconnectStrava} disabled={busy} variant="outline" className="border-border bg-transparent hover:bg-secondary">
                <Link2Off size={15} className="mr-2" /> Déconnecter Strava
              </Button>
            </div>
          ) : (
            <Button data-testid="strava-connect-btn" onClick={connectStrava} disabled={busy} className="mt-3 bg-[#FC4C02] hover:bg-[#FC4C02]/90 text-white rounded-md h-11 px-5 font-semibold">
              <Activity size={16} className="mr-2" /> {busy ? "Redirection…" : "Connecter Strava"}
            </Button>
          )}
        </div>

        {/* Profil & Nutrition (athlète) */}
        {user?.role !== "coach" && (
          <div className="bg-card border border-border rounded-xl p-5" data-testid="profile-nutrition-card">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center"><Salad className="text-green-400" size={20} /></span>
              <div>
                <h2 className="font-display text-xl leading-none">Profil & Nutrition</h2>
                <p className="text-xs text-muted-foreground mt-1">Mets à jour ta morphologie, tes allergies et préférences. Tes cibles macro et recettes se recalculent automatiquement.</p>
              </div>
            </div>
            {!prof ? (
              <div className="h-40 bg-secondary/60 rounded-md animate-pulse" />
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Field label="Poids (kg)"><Input data-testid="prof-weight" type="number" inputMode="decimal" value={prof.weight_kg} onChange={setP("weight_kg")} className="h-12 text-base bg-secondary border-border" /></Field>
                  <Field label="Taille (cm)"><Input data-testid="prof-height" type="number" inputMode="decimal" value={prof.height_cm} onChange={setP("height_cm")} className="h-12 text-base bg-secondary border-border" /></Field>
                  <Field label="Âge"><Input data-testid="prof-age" type="number" inputMode="numeric" value={prof.age} onChange={setP("age")} className="h-12 text-base bg-secondary border-border" /></Field>
                  <Field label="Vol. hebdo (h)"><Input data-testid="prof-hours" type="number" inputMode="decimal" value={prof.weekly_hours} onChange={setP("weekly_hours")} className="h-12 text-base bg-secondary border-border" /></Field>
                </div>
                <Field label="Genre"><Chips testid="prof-gender" options={["homme", "femme"]} value={[prof.gender]} onToggle={(v) => setProf((p) => ({ ...p, gender: v }))} /></Field>
                <Field label="Régime"><Chips testid="prof-diettype" options={DIET_TYPES} value={[prof.diet_type]} onToggle={(v) => setProf((p) => ({ ...p, diet_type: v }))} /></Field>
                <Field label="Allergies / intolérances"><Chips testid="prof-allergies" options={ALLERGENS} value={prof.allergies} onToggle={toggleAllergy} /></Field>
                <Field label="Autre allergie / intolérance"><Input data-testid="prof-allergies-other" placeholder="ex : kiwi, sésame…" value={prof.allergies_other} onChange={setP("allergies_other")} className="h-12 text-base bg-secondary border-border" /></Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Aliments appréciés"><Input data-testid="prof-liked" placeholder="poulet, riz, avoine…" value={prof.liked_foods} onChange={setP("liked_foods")} className="h-12 text-base bg-secondary border-border" /></Field>
                  <Field label="Aliments à éviter (goût)"><Input data-testid="prof-disliked" placeholder="brocoli, tofu…" value={prof.disliked_foods} onChange={setP("disliked_foods")} className="h-12 text-base bg-secondary border-border" /></Field>
                </div>
                <Button data-testid="prof-save-btn" onClick={saveProfile} disabled={savingProf} className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white rounded-md h-11 px-6 font-semibold">
                  {savingProf ? "Enregistrement…" : "Enregistrer les modifications"}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Parle-moi de toi + Objectif (athlète) */}
        {user?.role !== "coach" && prof && (
          <div className="bg-card border border-border rounded-xl p-5" data-testid="objective-card">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center"><Target className="text-primary" size={20} /></span>
              <div>
                <h2 className="font-display text-xl leading-none">Objectif & contexte</h2>
                <p className="text-xs text-muted-foreground mt-1">Ton contexte personnel guide l'IA. Un changement d'objectif est envoyé à ton coach, qui valide avant de relancer la génération.</p>
              </div>
            </div>
            <div className="space-y-4">
              <Field label="Parle-moi de toi (optionnel)">
                <Textarea data-testid="prof-about" rows={4}
                  placeholder="Tes objectifs, contraintes de vie (famille, travail), appréhensions ou points d'attention physiques/mentaux (ex : ancienne blessure au dos)…"
                  value={prof.about_me} onChange={setP("about_me")} className="text-base bg-secondary border-border resize-none" />
                <p className="text-[11px] text-muted-foreground mt-1.5">Enregistré avec le bouton « Enregistrer les modifications » ci-dessus. Pris en compte à la prochaine génération / régénération de ton programme.</p>
              </Field>

              <div className="border-t border-border pt-4">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Changer d'objectif de course</Label>
                {objPending ? (
                  <div className="mt-2 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3" data-testid="objective-pending">
                    <p className="text-sm text-amber-300 font-medium">Demande en attente de validation par ton coach</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Nouveau temps cible : <b className="text-foreground">{objPending.new_target_time || "—"}</b>
                      {objPending.new_race_date ? <> · Date : <b className="text-foreground">{objPending.new_race_date}</b></> : null}
                    </p>
                  </div>
                ) : (
                  <div className="mt-2 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Temps cible (hh:mm:ss)"><Input data-testid="obj-target" placeholder="ex : 1:15:00" value={obj.target_time} onChange={(e) => setObj((o) => ({ ...o, target_time: e.target.value }))} className="h-12 text-base bg-secondary border-border" /></Field>
                      <Field label="Date de course (AAAA-MM-JJ)"><Input data-testid="obj-date" type="date" value={obj.race_date} onChange={(e) => setObj((o) => ({ ...o, race_date: e.target.value }))} className="h-12 text-base bg-secondary border-border" /></Field>
                    </div>
                    <Field label="Message pour le coach (optionnel)"><Input data-testid="obj-note" placeholder="Pourquoi ce changement ?" value={obj.note} onChange={(e) => setObj((o) => ({ ...o, note: e.target.value }))} className="h-12 text-base bg-secondary border-border" /></Field>
                    <Button data-testid="obj-submit-btn" onClick={sendObjectiveChange} disabled={objBusy} variant="outline" className="w-full sm:w-auto border-primary/40 text-primary bg-transparent hover:bg-primary/10 rounded-md h-11 px-6 font-semibold">
                      {objBusy ? "Envoi…" : "Envoyer la demande au coach"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Subscription */}
        {user?.role !== "coach" && (
          <div className="bg-card border border-border rounded-xl p-5" data-testid="subscription-card">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center"><CreditCard className="text-primary" size={20} /></span>
              <div>
                <h2 className="font-display text-xl leading-none">Abonnement</h2>
                <p className="text-xs text-muted-foreground mt-1">Gère ton offre, ton moyen de paiement et tes factures.</p>
              </div>
            </div>
            <Button data-testid="manage-subscription-btn" onClick={manageSubscription} variant="outline" className="mt-3 border-border bg-transparent hover:bg-secondary">
              Gérer mon abonnement
            </Button>
          </div>
        )}

        <div className="text-center text-xs text-muted-foreground pt-2 space-x-3">
          <button onClick={() => nav("/legal")} className="hover:text-foreground">Mentions légales</button>
          <span>·</span>
          <button onClick={() => nav("/privacy")} className="hover:text-foreground">Confidentialité</button>
        </div>
      </main>
    </div>
  );
}
